export type ShiftFloodPreviewTriggerParams = {
  isDicomViewport: boolean;
  isBrushModifier: boolean;
  shiftDown: boolean;
};

export type ShiftFloodPreviewCommitParams = ShiftFloodPreviewTriggerParams & {
  hasActiveStroke: boolean;
};

export type ShiftFloodPreviewScheduleParams = ShiftFloodPreviewTriggerParams & {
  pointerInside: boolean;
  hasPointer: boolean;
  hasActiveStroke: boolean;
  hasPendingFrame: boolean;
};

export type ViewportRectLike = {
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export type ShiftFloodPreviewBootstrapParams =
  ShiftFloodPreviewTriggerParams & {
    clientXY: readonly [number, number] | null | undefined;
    viewportRect: ViewportRectLike;
    hasActiveStroke: boolean;
    hasPendingFrame: boolean;
  };

export type ShiftFloodPreviewDelayParams = {
  nowMs: number;
  lastCompletedAtMs: number;
  lastDurationMs: number;
  durationThresholdMs?: number;
  minIntervalMs?: number;
};

export type PreviewMaskConnect2DMode = 'shift' | 'tab';

export type PreviewMaskConnect2DCommitParams = {
  isDicomViewport: boolean;
  isBrushModifier: boolean;
  hasPreviewMask: boolean;
  previewMode: PreviewMaskConnect2DMode;
};

export type PreviewMaskConnect2DStrokeIndicesParams = Omit<
  PreviewMaskConnect2DCommitParams,
  'hasPreviewMask'
> & {
  strokeFlippedIndices: ArrayLike<number> | Iterable<number> | null | undefined;
};

export type BrushConnect2DSelectionRegionMeta = {
  seriesId: string;
  viewportId: string;
  sliceAxis: string;
  sliceIndex: number;
};

export type BrushConnect2DSelectionUpdateAction =
  | 'set-start'
  | 'reset-start'
  | 'roll-end-to-start'
  | 'merge-start'
  | 'set-end';

export type BrushConnect2DSelectionUpdateParams = {
  start: BrushConnect2DSelectionRegionMeta | null | undefined;
  end: BrushConnect2DSelectionRegionMeta | null | undefined;
  incoming: BrushConnect2DSelectionRegionMeta;
};

const SHIFT_FLOOD_PREVIEW_VIEWPORT_IDS = new Set([
  'Axial',
  'Coronal',
  'Sagittal',
]);

export function isShiftFloodPreviewViewport(viewportId: string): boolean {
  return SHIFT_FLOOD_PREVIEW_VIEWPORT_IDS.has(viewportId);
}

export function shouldUseShiftFloodPreview({
  isDicomViewport,
  isBrushModifier,
  shiftDown,
}: ShiftFloodPreviewTriggerParams): boolean {
  return isDicomViewport && isBrushModifier && shiftDown;
}

export function shouldCommitShiftFloodPreview({
  hasActiveStroke,
  ...triggerParams
}: ShiftFloodPreviewCommitParams): boolean {
  return shouldUseShiftFloodPreview(triggerParams) && !hasActiveStroke;
}

export function shouldScheduleShiftFloodPreview({
  pointerInside,
  hasPointer,
  hasActiveStroke,
  hasPendingFrame,
  ...triggerParams
}: ShiftFloodPreviewScheduleParams): boolean {
  return (
    shouldUseShiftFloodPreview(triggerParams) &&
    pointerInside &&
    hasPointer &&
    !hasActiveStroke &&
    !hasPendingFrame
  );
}

export function isClientXYInsideViewportRect(
  clientXY: readonly [number, number] | null | undefined,
  rect: ViewportRectLike,
): boolean {
  if (!clientXY) return false;

  const [clientX, clientY] = clientXY;
  return (
    Number.isFinite(clientX) &&
    Number.isFinite(clientY) &&
    clientX >= rect.left &&
    clientX <= rect.right &&
    clientY >= rect.top &&
    clientY <= rect.bottom
  );
}

export function shouldBootstrapShiftFloodPreview({
  clientXY,
  viewportRect,
  hasActiveStroke,
  hasPendingFrame,
  ...triggerParams
}: ShiftFloodPreviewBootstrapParams): boolean {
  const pointerInside = isClientXYInsideViewportRect(clientXY, viewportRect);
  return shouldScheduleShiftFloodPreview({
    ...triggerParams,
    pointerInside,
    hasPointer: !!clientXY,
    hasActiveStroke,
    hasPendingFrame,
  });
}

export function shouldCommitPreviewMaskToConnect2D({
  isDicomViewport,
  isBrushModifier,
  hasPreviewMask,
}: PreviewMaskConnect2DCommitParams): boolean {
  return isDicomViewport && isBrushModifier && hasPreviewMask;
}

export function getPreviewMaskConnect2DStrokeIndices({
  strokeFlippedIndices,
  ...commitParams
}: PreviewMaskConnect2DStrokeIndicesParams): number[] {
  const indices = strokeFlippedIndices
    ? Array.from(strokeFlippedIndices)
    : [];

  if (
    !shouldCommitPreviewMaskToConnect2D({
      ...commitParams,
      hasPreviewMask: indices.length > 0,
    })
  ) {
    return [];
  }

  return indices;
}

function isSameBrushConnect2DRegionMeta(
  region: BrushConnect2DSelectionRegionMeta,
  target: BrushConnect2DSelectionRegionMeta,
) {
  return (
    region.seriesId === target.seriesId &&
    region.viewportId === target.viewportId &&
    region.sliceAxis === target.sliceAxis &&
    region.sliceIndex === target.sliceIndex
  );
}

export function getBrushConnect2DSelectionUpdateAction({
  start,
  end,
  incoming,
}: BrushConnect2DSelectionUpdateParams): BrushConnect2DSelectionUpdateAction {
  if (!start) return 'set-start';

  if (
    start.viewportId !== incoming.viewportId ||
    start.sliceAxis !== incoming.sliceAxis
  ) {
    return 'reset-start';
  }

  if (end && !isSameBrushConnect2DRegionMeta(end, incoming)) {
    return 'roll-end-to-start';
  }

  if (isSameBrushConnect2DRegionMeta(start, incoming)) {
    return 'merge-start';
  }

  return 'set-end';
}

export function getShiftFloodPreviewDelayMs({
  nowMs,
  lastCompletedAtMs,
  lastDurationMs,
  durationThresholdMs = 12,
  minIntervalMs = 32,
}: ShiftFloodPreviewDelayParams): number {
  if (
    !Number.isFinite(nowMs) ||
    !Number.isFinite(lastCompletedAtMs) ||
    !Number.isFinite(lastDurationMs) ||
    lastCompletedAtMs <= 0 ||
    lastDurationMs < durationThresholdMs
  ) {
    return 0;
  }

  const elapsedMs = nowMs - lastCompletedAtMs;
  if (!Number.isFinite(elapsedMs) || elapsedMs >= minIntervalMs) {
    return 0;
  }

  return Math.max(0, minIntervalMs - elapsedMs);
}

export function previewIJKsToLinearIndices(
  ijks: ReadonlyArray<readonly [number, number, number]>,
  dimensions: readonly number[],
): number[] {
  return ijks.map(([i, j, k]) => (k * dimensions[1] + j) * dimensions[0] + i);
}

export function diffPreviewIJKs(
  previousIJKs: ReadonlyArray<readonly [number, number, number]> | null | undefined,
  nextIJKs: ReadonlyArray<readonly [number, number, number]> | null | undefined,
  dimensions: readonly number[],
): {
  clearIJKs: Array<readonly [number, number, number]>;
  paintIJKs: Array<readonly [number, number, number]>;
} {
  const previous = previousIJKs ?? [];
  const next = nextIJKs ?? [];

  if (!previous.length) {
    return { clearIJKs: [], paintIJKs: [...next] };
  }
  if (!next.length) {
    return { clearIJKs: [...previous], paintIJKs: [] };
  }

  return diffPreviewIJKsByLinearIndex(
    previous,
    previewIJKsToLinearIndices(previous, dimensions),
    next,
    previewIJKsToLinearIndices(next, dimensions),
  );
}

export function diffPreviewIJKsByLinearIndex(
  previousIJKs: ReadonlyArray<readonly [number, number, number]> | null | undefined,
  previousLinearIndices: ReadonlyArray<number> | null | undefined,
  nextIJKs: ReadonlyArray<readonly [number, number, number]> | null | undefined,
  nextLinearIndices: ReadonlyArray<number> | null | undefined,
): {
  clearIJKs: Array<readonly [number, number, number]>;
  paintIJKs: Array<readonly [number, number, number]>;
} {
  const previous = previousIJKs ?? [];
  const previousIndices = previousLinearIndices ?? [];
  const next = nextIJKs ?? [];
  const nextIndices = nextLinearIndices ?? [];

  if (!previous.length) {
    return { clearIJKs: [], paintIJKs: [...next] };
  }
  if (!next.length) {
    return { clearIJKs: [...previous], paintIJKs: [] };
  }

  const previousIndexSet = new Set(previousIndices);
  const nextIndexSet = new Set(nextIndices);

  return {
    clearIJKs: previous.filter((_, index) => {
      return !nextIndexSet.has(previousIndices[index]);
    }),
    paintIJKs: next.filter((_, index) => {
      return !previousIndexSet.has(nextIndices[index]);
    }),
  };
}

export function diffPreviewLinearIndices(
  previousLinearIndices: ReadonlyArray<number> | null | undefined,
  nextLinearIndices: ReadonlyArray<number> | null | undefined,
): {
  clearLinearIndices: number[];
  paintLinearIndices: number[];
} {
  const previous = previousLinearIndices ?? [];
  const next = nextLinearIndices ?? [];

  if (!previous.length) {
    return { clearLinearIndices: [], paintLinearIndices: [...next] };
  }
  if (!next.length) {
    return { clearLinearIndices: [...previous], paintLinearIndices: [] };
  }

  const previousSet = new Set(previous);
  const nextSet = new Set(next);

  return {
    clearLinearIndices: previous.filter((index) => !nextSet.has(index)),
    paintLinearIndices: next.filter((index) => !previousSet.has(index)),
  };
}
